import React from "react";
import { Loader2, Upload, Wand2, Sparkles, X } from "lucide-react";

export const PhotoMatchModal = ({
  open,
  onClose,
  pmImage,
  pmBusy,
  pmResult,
  pmFileRef,
  onPmFile,
  onReplace,
  runPhotoMatch,
  onTryOnFromMatch,
  trackAdClick,
  selectedPhoto,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#F6F5F1] border border-black/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded-full z-10">
          <X className="w-4 h-4"/>
        </button>
        <div className="p-8 md:p-12">
          <div className="font-mono-eyebrow">Muse Photo Match</div>
          <h3 className="font-serif-display text-4xl md:text-5xl leading-none mt-2 mb-3">Show Muse a look you love.</h3>
          <p className="text-black/60 max-w-lg">
            Screenshot from Instagram, Pinterest, a street-style blog — anywhere. Muse decodes the vibe, then finds the closest pieces in your wardrobe and completes the gaps from the marketplace.
          </p>

          <div className="grid grid-cols-12 gap-6 mt-8">
            <div className="col-span-12 md:col-span-5">
              <input ref={pmFileRef} type="file" accept="image/*" hidden onChange={onPmFile} data-testid="photo-match-file"/>
              {!pmImage ? (
                <button onClick={() => pmFileRef.current?.click()} className="w-full aspect-[3/4] border border-dashed border-black/30 flex flex-col items-center justify-center gap-3 hover:border-black transition-colors bg-white">
                  <Upload className="w-6 h-6"/>
                  <div className="font-mono-eyebrow">Upload inspiration</div>
                  <div className="text-xs text-black/40 max-w-[220px] text-center">JPG or PNG · full-body shot works best</div>
                </button>
              ) : (
                <div className="relative">
                  <div className="aspect-[3/4] overflow-hidden bg-white polaroid">
                    <img src={pmImage} alt="inspiration" className="w-full h-full object-cover"/>
                  </div>
                  <button onClick={onReplace} className="absolute top-2 right-2 bg-white/90 backdrop-blur px-3 py-1 font-mono-eyebrow text-[10px] hover:bg-black hover:text-white transition-colors">
                    Replace
                  </button>
                </div>
              )}
              <button data-testid="photo-match-run" onClick={runPhotoMatch} disabled={!pmImage || pmBusy}
                className="btn-ink w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50">
                {pmBusy ? <><Loader2 className="w-4 h-4 animate-spin"/> Decoding…</> : <><Wand2 className="w-4 h-4"/> Find matches</>}
              </button>
            </div>

            <div className="col-span-12 md:col-span-7">
              {!pmResult && !pmBusy && (
                <div className="h-full flex flex-col items-center justify-center text-black/40 border border-black/10 p-8 min-h-[280px]">
                  <div className="font-serif-display text-3xl text-center leading-tight">Awaiting the reference</div>
                  <div className="font-mono-eyebrow mt-3 text-center">Upload a photo, then Muse gets to work</div>
                </div>
              )}
              {pmBusy && (
                <div className="h-full flex flex-col items-center justify-center gap-3 border border-black/10 p-8 min-h-[280px]">
                  <div className="w-10 h-10 border-2 border-black border-t-transparent rounded-full animate-spin"/>
                  <div className="font-mono-eyebrow">Muse is reading the room…</div>
                </div>
              )}
              {pmResult && (
                <div className="space-y-6" data-testid="photo-match-result">
                  <div>
                    <div className="font-mono-eyebrow text-[color:var(--accent)]">The vibe</div>
                    <p className="font-serif-display text-2xl leading-tight mt-1">{pmResult.vibe}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {pmResult.style_tags.map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 border border-black/20 font-mono-eyebrow capitalize">{t}</span>
                      ))}
                    </div>
                    {pmResult.color_palette?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {pmResult.color_palette.map((c, i) => (
                          <span key={`${c}-${i}`} className="text-[10px] px-2 py-0.5 bg-black/5 font-mono-eyebrow">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {pmResult.wardrobe_matches?.length > 0 && (
                    <div>
                      <div className="font-mono-eyebrow mb-2">From your wardrobe · pre-selected for try-on</div>
                      <div className="grid grid-cols-3 gap-2">
                        {pmResult.wardrobe_matches.map((it) => (
                          <div key={it.id} data-testid={`pm-ward-${it.id}`} className="aspect-[3/4] overflow-hidden ring-2 ring-[color:var(--accent)] bg-white">
                            <img src={it.image_url} alt={it.title} className="w-full h-full object-cover"/>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pmResult.marketplace_matches?.length > 0 && (
                    <div>
                      <div className="font-mono-eyebrow mb-2 text-[color:var(--accent)]">Complete the look · Sponsored</div>
                      <div className="grid grid-cols-3 gap-2">
                        {pmResult.marketplace_matches.map((a) => (
                          <a key={a.id} href={a.target_url} target="_blank" rel="noopener noreferrer sponsored"
                            data-testid={`pm-shop-${a.id}`}
                            onClick={() => trackAdClick(a.id)}
                            className="group relative bg-white border border-black/10 block hover:border-black transition-colors">
                            <div className="aspect-[3/4] overflow-hidden"><img src={a.image_url} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/></div>
                            <div className="p-2">
                              <div className="font-mono-eyebrow text-black/40 text-[9px]">{a.brand}</div>
                              <div className="font-serif-display text-xs leading-tight line-clamp-2">{a.title}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-black/60 leading-relaxed border-t border-black/10 pt-4" data-testid="photo-match-reasoning">{pmResult.reasoning}</p>

                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={onClose} className="btn-outline-ink">Close</button>
                    <button data-testid="photo-match-tryon" onClick={onTryOnFromMatch}
                      disabled={!pmResult.wardrobe_matches?.length || !selectedPhoto}
                      className="btn-ink flex items-center gap-2 disabled:opacity-50">
                      <Sparkles className="w-3.5 h-3.5"/> Try this look on
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
